import React, { useEffect, useState } from "react";
import { FaTags } from "react-icons/fa";
import { enqueueSnackbar } from "notistack";
import CardHeader from "../../../components/Card/CardHeader";
import Loader from "../../../components/Loader/Loader";
import Modal from "../../../components/Modal/Modal";
import Table from "../../../components/Table/Table";
import { getPrograms } from "../../../services/ProgramServices";
import {
  createMeetingWithType,
  deleteMeetingWithType,
  getMeetingWithTypes,
  updateMeetingWithType,
} from "../../../services/AppManagement/MeetingWithTypeService";
import { tableConstants } from "./MeetingWithTypeTableConstant";
import MeetingWithTypeForm from "./MeetingWithTypeForm";
import "./MeetingWithType.css";
import Button from "../../../components/Button/Button";

const MeetingWithTypeList = () => {
  const [data, setData] = useState([]);
  const [isLoader, setIsLoader] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isProgramsModalOpen, setIsProgramsModalOpen] = useState(false);
  const [programOptions, setProgramOptions] = useState([]);
  const [selectedProgramIds, setSelectedProgramIds] = useState([]);
  const [programPreview, setProgramPreview] = useState([]);
  const [name, setName] = useState("");
  const [id, setId] = useState(null);
  const permissions = JSON.parse(localStorage.getItem("permissions") || "[]");

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  const resetForm = () => {
    setName("");
    setId(null);
    setSelectedProgramIds([]);
  };

  const fetchMeetingWithTypes = async () => {
    try {
      setIsLoader(true);
      const response = await getMeetingWithTypes();
      if (response.status === 200) {
        setData(response.data?.data || response.data || []);
      }
    } catch (error) {
      console.error("API Error:", error);
      enqueueSnackbar("Failed to fetch meeting with types", { variant: "error" });
    } finally {
      setIsLoader(false);
    }
  };

  const fetchPrograms = async () => {
    try {
      const response = await getPrograms();
      if (response?.success) {
        setProgramOptions(response.data || []);
      }
    } catch (error) {
      console.error("API Error:", error);
    }
  };

  useEffect(() => {
    fetchMeetingWithTypes();
    fetchPrograms();
  }, []);

  const changeHandler = (e) => {
    const { name: fieldName, value } = e.target;
    if (fieldName === "name") {
      setName(value);
    }
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const organizationId = localStorage.getItem("organization_id");
    if (!organizationId) {
      enqueueSnackbar("Select any organization first", { variant: "error" });
      return;
    }

    const payload = {
      organization_id: Number(organizationId),
      name: name.trim(),
      program_ids: selectedProgramIds.map((item) => Number(item)),
    };
    if (payload.program_ids.length === 0) {
      enqueueSnackbar("Please select at least one class", { variant: "error" });
      return;
    }


    if (!payload.name) {
      enqueueSnackbar("Name is required", { variant: "error" });
      return;
    }

    try {
      setIsLoader(true);
      let response;

      if (id) {
        response = await updateMeetingWithType(id, payload);
      } else {
        response = await createMeetingWithType(payload);
      }

      if ([200, 201].includes(response.status)) {
        enqueueSnackbar(response?.data?.message?.success || "Saved successfully", {
          variant: "success",
        });
        closeModal();
        resetForm();
        fetchMeetingWithTypes();
      }
    } catch (error) {
      console.error("API Error:", error);
      enqueueSnackbar("Something went wrong", { variant: "error" });
    } finally {
      setIsLoader(false);
    }
  };

  const handleEdit = (rowData) => {
    setId(rowData.id);
    setName(rowData.name || "");
    const ids = Array.isArray(rowData.program_ids) && rowData.program_ids.length > 0
      ? rowData.program_ids.map((programId) => programId.toString())
      : (rowData.programs || [])
          .map((item) => item?.id || item?.program?.id)
          .filter((programId) => programId !== undefined && programId !== null)
          .map((programId) => programId.toString());
    setSelectedProgramIds(ids);
    openModal();
  };

  const handleProgramToggle = (event) => {
    const progId = event.target.value.toString();
    if (event.target.checked) {
      setSelectedProgramIds((prev) => Array.from(new Set([...prev, progId])));
      return;
    }

    setSelectedProgramIds((prev) => prev.filter((idVal) => idVal !== progId));
  };

  const handleSelectAllPrograms = (event) => {
    if (event.target.checked) {
      setSelectedProgramIds(programOptions.map((program) => program.id.toString()));
      return;
    }
    setSelectedProgramIds([]);
  };

  const handleProgramsPreview = (rowData) => {
    const names = (rowData.programs || [])
      .map((item) => item?.name || item?.program?.name)
      .filter(Boolean);
    setProgramPreview(names);
    setIsProgramsModalOpen(true);
  };

  const handleCreateClick = () => {
    resetForm();
    openModal();
  };

  const handleDelete = async (rowData) => {
    try {
      setIsLoader(true);
      const response = await deleteMeetingWithType(rowData.id);
      if (response.status === 200) {
        enqueueSnackbar(response?.data?.message?.success || "Deleted successfully", {
          variant: "success",
        });
        fetchMeetingWithTypes();
      }
    } catch (error) {
      console.error("API Error:", error);
      enqueueSnackbar("Failed to delete", { variant: "error" });
    } finally {
      setIsLoader(false);
    }
  };

  return (
    <div className="card">
      <CardHeader
        title="Meeting With Types"
        icon={<FaTags />}
        button={
          permissions.includes("meeting.with.type.create") && (
            <Button type="submit" variant="primary" onClick={handleCreateClick}>
              Add Meeting With Type
            </Button>
          )
        }
      />

      <div className="card-body">
        <Modal isOpen={isModalOpen} onClose={closeModal}>
          <h5>{id ? "UPDATE MEETING WITH TYPE" : "ADD MEETING WITH TYPE"}</h5>
          <MeetingWithTypeForm
            submitHandler={submitHandler}
            changeHandler={changeHandler}
            name={name}
            programOptions={programOptions}
            selectedProgramIds={selectedProgramIds}
            onProgramToggle={handleProgramToggle}
            onSelectAllPrograms={handleSelectAllPrograms}
          />
        </Modal>

        <Modal isOpen={isProgramsModalOpen} onClose={() => setIsProgramsModalOpen(false)} size="lg">
          <div className="meeting-preview-header mb-2">
            <div>
              <h5 className="mb-0">MAPPED PROGRAMS</h5>
              <p className="meeting-preview-subtitle mb-0">Programs linked with this meeting type.</p>
            </div>
            <span className="meeting-preview-count">{programPreview.length} Selected</span>
          </div>

          {programPreview.length > 0 ? (
            <div className="meeting-preview-list">
              <div className="row g-2">
                {programPreview.map((programName, index) => (
                  <div className="col-md-6" key={`${programName}-${index}`}>
                    <div className="meeting-preview-item">
                      <span className="meeting-preview-index">{index + 1}</span>
                      <span className="meeting-preview-name">{programName}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="alert alert-light border mb-0">No programs mapped.</div>
          )}
        </Modal>

        {isLoader ? (
          <Loader />
        ) : (
          <div className="row mt-3">
            <Table
              cols={tableConstants(permissions, handleEdit, handleDelete, handleProgramsPreview)}
              data={data}
              hoverable={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default MeetingWithTypeList;
